import React, { useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { setAuthCookie } from '../../utils/auth'

const Form = styled.form`
  margin-top: 0px;

  .passwordInline {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 16px;
    width: 100%;
    user-select: none;
  }

  .passwordField {
    flex: 1;
    min-width: 0;
    position: relative;
  }

  .passwordTrigger {
    display: block;
    opacity: 0.95;
    pointer-events: none;
  }

  .passwordInput {
    background: transparent;
    border: 0;
    outline: none;
    padding: 0;
    margin: 0;
    color: inherit;
    font: inherit;
    line-height: inherit;
    min-width: 0;
    flex: 1;

    /* Prevent iOS Safari focus zoom */
    font-size: 16px;
  }

  .passwordInput.is-empty {
    inset: 0;
    opacity: 0;
    position: absolute;
    width: 100%;
  }

  .passwordInput::placeholder {
    color: inherit;
    opacity: 0.95;
  }

  .passwordInput::-webkit-credentials-auto-fill-button,
  .passwordInput::-webkit-contacts-auto-fill-button {
    visibility: hidden;
    display: none !important;
    pointer-events: none;
    position: absolute;
    right: 0;
  }

  .passwordArrow {
    background: transparent;
    border: 0;
    padding: 0;
    margin: 0;
    color: inherit;
    font: inherit;
    cursor: pointer;
    opacity: 0.95;
    white-space: nowrap;
  }

  .passwordArrow:disabled {
    cursor: default;
    opacity: 0.6;
  }

  .passwordArrow.is-hidden {
    display: none;
  }

  .passwordArrow:hover,
  .passwordArrow:focus-visible {
    opacity: 1;
  }

  .passwordInline:focus-within .passwordArrow {
    opacity: 1;
  }

  .error {
    margin-top: 10px;
    opacity: 0.85;
    font-size: 12px;
  }
`

function PasswordGate({ expectedPassword, inputId: inputIdProp, onSuccess, textColor }) {
  const inputRef = useRef(null)
  const [password, setPassword] = useState('')
  const [active, setActive] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const fallbackInputId = useMemo(() => 'site-access-code', [])
  const inputId = inputIdProp || fallbackInputId

  const submit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    const expected = String(
      expectedPassword || process.env.REACT_APP_SITE_PASSWORD || '',
    ).trim()

    if (!expected) {
      setError('Missing site password.')
      setLoading(false)
      return
    }

    if (password.trim() !== expected) {
      setError('Incorrect password.')
      setLoading(false)
      return
    }

    // Blur active element (fixes Safari zoom/focus state)
    if (
      document.activeElement &&
      typeof document.activeElement.blur === 'function'
    ) {
      document.activeElement.blur()
    }

    setAuthCookie(30)
    setLoading(false)

    if (typeof onSuccess === 'function') {
      onSuccess()
    }
  }

  return (
    <Form onSubmit={submit} style={{ color: textColor }} className="border-top">
      <div className="passwordInline">
        <div className="passwordField">
          {!active && !password && (
            <span className="passwordTrigger">Password</span>
          )}

          <input
            ref={inputRef}
            id={inputId}
            name="site-access-code"
            type="text"
            className={`passwordInput${!active && !password ? ' is-empty' : ''}`}
            value={password}
            onFocus={() => setActive(true)}
            onBlur={() => {
              if (!password) setActive(false)
            }}
            onChange={(e) => {
              const value = e.target.value
              if (value.length <= 64) setPassword(value)
            }}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            maxLength={64}
            inputMode="text"
            aria-label="Access code"
            style={{ WebkitTextSecurity: password ? 'disc' : 'none' }}
          />
        </div>

        <button
          type="submit"
          className={`passwordArrow${active || password ? '' : ' is-hidden'}`}
          disabled={loading}
          aria-label="Unlock"
          title="Unlock"
          onMouseDown={(e) => e.preventDefault()}
        >
          {loading ? 'Checking...' : 'Enter ↗'}
        </button>
      </div>

      {error && <div className="error">{error}</div>}
    </Form>
  )
}

export default PasswordGate
